import React, { useState, useRef, useEffect } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth, functions } from './firebase';
import downscaleImage from './downscaleImage';
import { SOCIAL_PLATFORMS, detectPlatform, buildSocialUrl, platformLabel as getPlatformLabel } from './platforms';

// ── Invite helpers ────────────────────────────────────────────────────────────

function generateToken() {
  // Cryptographically strong, unguessable invite token (an invite that's guessed
  // can be claimed by anyone and links the inviter's tile to the claimer).
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  return Array.from(a, b => b.toString(16).padStart(2, '0')).join('');
}

const SHARE_CHANNELS = [
  { id: 'copy',     label: 'Copy Link'   },
  { id: 'sms',      label: 'Text'        },
  { id: 'email',    label: 'Email'       },
  { id: 'twitter',  label: 'X / Twitter' },
  { id: 'facebook', label: 'Facebook'    },
  { id: 'threads',  label: 'Threads'     },
];

async function createInvite(entry, fromProfile) {
  const token = generateToken();
  const expires = new Date();
  expires.setDate(expires.getDate() + 30);
  await setDoc(doc(db, 'invites', token), {
    fromUid: fromProfile.uid,
    fromUsername: fromProfile.username,
    fromDisplayName: fromProfile.displayName,
    fromPhotoURL: fromProfile.photoURL || null,
    tileId: entry.id,
    tileName: entry.displayName,
    status: 'pending',
    createdAt: new Date(),
    expiresAt: expires,
    claimedBy: null,
  });
  return token;
}

function shareInvite(channel, url, fromName, inviteUrl, setCopied) {
  const msg = `Hey! ${fromName} just added you to their Top 8 on Your Top 8. Join to see your rank, get notified when it changes, and build your own! ${inviteUrl}`;
  switch (channel) {
    case 'copy':
      navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    case 'sms':
      window.open(`sms:?body=${encodeURIComponent(msg)}`, '_self');
      return;
    case 'email':
      window.open(`mailto:?subject=${encodeURIComponent(`${fromName} added you to their Top 8!`)}&body=${encodeURIComponent(msg)}`);
      return;
    case 'twitter':
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(msg)}`, '_blank');
      return;
    case 'facebook':
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
      return;
    case 'threads':
      window.open(`https://www.threads.net/intent/post?text=${encodeURIComponent(msg)}`, '_blank');
      return;
    default:
      navigator.clipboard.writeText(inviteUrl);
  }
}

// ── Utilities ─────────────────────────────────────────────────────────────────

function genId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function connLabel(conn) {
  if (conn.type === 'PROFILE') return `@${conn.value}`;
  if (conn.type === 'TEXT') return conn.value;
  return getPlatformLabel(conn.value);
}

function connIcon(conn) {
  if (conn.type === 'PROFILE') return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  );
  if (conn.type === 'TEXT') return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
    </svg>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

function PersonConfigModal({ entry, onSave, onRemove, onClose, currentUserProfile, onOpenConnection }) {
  const [displayName, setDisplayName] = useState(entry.displayName || '');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(entry.photoURL || null);
  const [connections, setConnections] = useState(
    entry.connections?.length ? entry.connections : []
  );
  const [primaryAction, setPrimaryAction] = useState(entry.primaryAction || 'PROFILE');
  const [primaryValue, setPrimaryValue] = useState(entry.primaryValue || '');
  const [saving, setSaving] = useState(false);

  // Add connection form
  const [showAddConn, setShowAddConn] = useState(false);
  const [newConnType, setNewConnType] = useState('SOCIAL');
  const [newConnValue, setNewConnValue] = useState('');
  const [newConnPlatform, setNewConnPlatform] = useState(''); // chosen social platform key
  const [newConnError, setNewConnError] = useState('');
  const [newConnSearching, setNewConnSearching] = useState(false);

  // Invite
  const [showInvite, setShowInvite] = useState(false);
  const [inviteUrl, setInviteUrl] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);

  const photoRef = useRef();
  const hasYt8 = connections.some(c => c.type === 'PROFILE');

  // A tile linked to a real Your Top 8 user mirrors THEIR profile. Its name and
  // photo are not the owner's to override (that would let anyone misrepresent a
  // real user on their own Top 8), so we lock both fields here and only allow it
  // for prospects / non-YT8 people. Editing stays available for connections,
  // primary action, etc.
  const lockedIdentity = !!(entry.yt8Username || entry.isRegisteredUser);

  // For a locked tile, pull the friend's CURRENT name/photo so the editor shows
  // the truth and a Save refreshes a stale snapshot (the only refresh path now
  // that manual override is gone). Falls back to the stored values if the read
  // fails. usernames/{username} -> users/{uid} is the same lookup the rest of the
  // app uses and is publicly readable for accepted/public profiles.
  useEffect(() => {
    if (!lockedIdentity || !entry.yt8Username) return;
    let active = true;
    (async () => {
      try {
        const uSnap = await getDoc(doc(db, 'usernames', entry.yt8Username));
        if (!active || !uSnap.exists()) return;
        const fSnap = await getDoc(doc(db, 'users', uSnap.data().uid));
        if (!active || !fSnap.exists()) return;
        const d = fSnap.data();
        if (d.displayName) setDisplayName(d.displayName);
        setPhotoPreview(d.photoURL || null);
      } catch { /* keep the stored snapshot */ }
    })();
    return () => { active = false; };
  }, [lockedIdentity, entry.yt8Username]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPrimary = (conn) =>
    conn.type === primaryAction && conn.value === primaryValue;

  const handleSetPrimary = (conn) => {
    setPrimaryAction(conn.type);
    setPrimaryValue(conn.value);
  };

  const handleRemoveConn = (connId) => {
    const removed = connections.find(c => c.id === connId);
    const remaining = connections.filter(c => c.id !== connId);
    setConnections(remaining);
    if (removed && isPrimary(removed) && remaining.length > 0) {
      setPrimaryAction(remaining[0].type);
      setPrimaryValue(remaining[0].value);
    }
  };

  const handleAddConn = async () => {
    const val = newConnValue.trim();
    if (!val) return;
    setNewConnError('');

    if (newConnType === 'PROFILE') {
      setNewConnSearching(true);
      const snap = await getDoc(doc(db, 'usernames', val.toLowerCase().replace('@', '')));
      setNewConnSearching(false);
      if (!snap.exists()) { setNewConnError('No Your Top 8 user found with that username.'); return; }
      const username = val.toLowerCase().replace('@', '');
      if (connections.some(c => c.type === 'PROFILE' && c.value === username)) {
        setNewConnError('Already linked.'); return;
      }
      const conn = { id: genId(), type: 'PROFILE', value: username };
      setConnections(prev => [...prev, conn]);
      if (!connections.length) { setPrimaryAction('PROFILE'); setPrimaryValue(username); }
    } else if (newConnType === 'TEXT') {
      const digits = val.replace(/[\s\-().+]/g, '');
      if (!/^\d{7,15}$/.test(digits)) { setNewConnError('Enter a valid phone number.'); return; }
      const hasPlus = val.trimStart().startsWith('+');
      const normalized = hasPlus ? `+${digits}` : digits;
      const conn = { id: genId(), type: 'TEXT', value: normalized };
      setConnections(prev => [...prev, conn]);
      if (!connections.length) { setPrimaryAction('TEXT'); setPrimaryValue(normalized); }
    } else {
      // Smart social: a $cashtag or a pasted link auto-detects the platform;
      // a bare handle uses whatever's selected in the dropdown.
      const key = detectPlatform(val) || newConnPlatform;
      if (!key) { setNewConnError('Pick a platform for that username.'); return; }
      const url = buildSocialUrl(key, val);
      if (!url) { setNewConnError('That platform isn\'t supported yet.'); return; }
      if (connections.some(c => c.type === 'SOCIAL' && c.value === url)) {
        setNewConnError('Already added.'); return;
      }
      const conn = { id: genId(), type: 'SOCIAL', value: url };
      setConnections(prev => [...prev, conn]);
      if (!connections.length) { setPrimaryAction('SOCIAL'); setPrimaryValue(url); }
    }

    setNewConnValue('');
    setNewConnPlatform('');
    setShowAddConn(false);
  };

  const handleSave = async () => {
    if (!displayName.trim()) return;
    setSaving(true);
    try {
      let photoURL = entry.photoURL || null;
      if (lockedIdentity) {
        // Mirror the linked user's live profile photo (loaded into photoPreview);
        // never an owner-uploaded override.
        photoURL = photoPreview || null;
      } else if (photoFile) {
        const uid = auth.currentUser?.uid;
        const fileRef = storageRef(storage, `top8-photos/${uid}/${Date.now()}`);
        const blob = await downscaleImage(photoFile, 512);
        await uploadBytes(fileRef, blob);
        photoURL = await getDownloadURL(fileRef);
      }

      const yt8Conn = connections.find(c => c.type === 'PROFILE');
      let connectionStatus = entry.connectionStatus || null;

      if (yt8Conn && !connectionStatus) {
        // New YT8 connection — look up recipient and send a (rate-limited) request
        // through the requestConnection function (server creates the pending doc
        // + notification).
        const uSnap = await getDoc(doc(db, 'usernames', yt8Conn.value));
        if (uSnap.exists()) {
          const recipientUid = uSnap.data().uid;
          if (recipientUid !== auth.currentUser?.uid) {
            try {
              const fn = httpsCallable(functions, 'requestConnection');
              await fn({ toUid: recipientUid, tileId: entry.id });
              connectionStatus = 'pending';
            } catch (e) { console.error('connect request failed', e); }
          }
        }
      }

      if (!yt8Conn) connectionStatus = null;

      onSave(entry.id, {
        displayName: displayName.trim(),
        photoURL,
        connections,
        primaryAction,
        primaryValue,
        yt8Username: yt8Conn?.value || null,
        isRegisteredUser: !!yt8Conn,
        connectionStatus,
      });
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  const handleGenerateInvite = async () => {
    if (!currentUserProfile) return;
    setInviteLoading(true);
    try {
      const token = await createInvite(entry, currentUserProfile);
      setInviteUrl(`${window.location.origin}/invite/${token}`);
    } catch (e) { console.error(e); }
    setInviteLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Edit Tile</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 20, cursor: 'pointer', padding: 4 }}>✕</button>
        </div>

        {/* Photo + name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: lockedIdentity ? 8 : 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, flexShrink: 0 }}>
            <div
              className="photo-picker-avatar"
              onClick={lockedIdentity ? undefined : () => photoRef.current?.click()}
              style={{ width: 56, height: 56, flexShrink: 0, cursor: lockedIdentity ? 'default' : 'pointer' }}
            >
              {photoPreview
                ? <img src={photoPreview} alt="preview" />
                : (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4 }}>
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                )
              }
            </div>
            {!lockedIdentity && (
              <button
                type="button"
                onClick={() => photoRef.current?.click()}
                style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11, cursor: 'pointer', padding: 0, fontFamily: 'var(--font)', whiteSpace: 'nowrap' }}
              >
                {photoPreview ? 'Change' : 'Add photo'}
              </button>
            )}
          </div>
          {lockedIdentity ? (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {displayName || '…'}
              </div>
            </div>
          ) : (
            <input
              className="input"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Display name"
              style={{ flex: 1 }}
            />
          )}
          <input ref={photoRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => {
              const f = e.target.files[0];
              if (!f) return;
              setPhotoFile(f);
              setPhotoPreview(URL.createObjectURL(f));
            }}
          />
        </div>
        {lockedIdentity && (
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: -2, marginBottom: 16 }}>
            Name and photo come from their Your Top 8 profile.
          </div>
        )}

        {/* Connections */}
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--muted)', marginBottom: 8 }}>
            Connections
          </div>

          {connections.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--muted)', padding: '10px 0' }}>
              No connections yet. Add one below.
            </div>
          )}

          {connections.map(conn => {
            const primary = isPrimary(conn);
            return (
              <div key={conn.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', marginBottom: 6,
                background: primary ? 'rgba(106,166,255,.08)' : 'var(--card-2)',
                border: `1.5px solid ${primary ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 10, cursor: 'pointer',
              }}
                onClick={() => handleSetPrimary(conn)}
                title="Click to set as primary action"
              >
                {/* Primary indicator */}
                <div style={{
                  width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                  border: `2px solid ${primary ? 'var(--accent)' : 'var(--border)'}`,
                  background: primary ? 'var(--accent)' : 'transparent',
                }} />

                <span style={{ fontSize: 16 }}>{connIcon(conn)}</span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: primary ? 700 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {connLabel(conn)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                    {conn.type === 'PROFILE' ? 'Your Top 8 profile'
                      : conn.type === 'TEXT' ? 'Text message'
                      : 'Social link'}
                    {primary && <span style={{ color: 'var(--accent)', marginLeft: 6 }}>· Primary</span>}
                  </div>
                </div>

                {conn.value && onOpenConnection && (
                  <button
                    onClick={e => { e.stopPropagation(); onOpenConnection(conn); }}
                    style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--accent)', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 8, flexShrink: 0 }}
                    title="Open this link"
                  >Open</button>
                )}
                <button
                  onClick={e => { e.stopPropagation(); handleRemoveConn(conn.id); }}
                  style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 16, padding: '0 2px', flexShrink: 0 }}
                  title="Remove"
                >×</button>
              </div>
            );
          })}

          {/* Add connection */}
          {!showAddConn ? (
            <button
              className="btn btn-sm"
              onClick={() => setShowAddConn(true)}
              style={{ marginTop: 4 }}
            >
              + Add Connection
            </button>
          ) : (
            <div style={{ background: 'var(--card-2)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, marginTop: 6 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                {['PROFILE', 'SOCIAL', 'TEXT'].map(t => (
                  <button
                    key={t}
                    className={`btn btn-sm${newConnType === t ? ' btn-primary' : ''}`}
                    onClick={() => { setNewConnType(t); setNewConnValue(''); setNewConnPlatform(''); setNewConnError(''); }}
                  >
                    {t === 'PROFILE' ? 'Profile' : t === 'SOCIAL' ? 'Social Link' : 'Phone / Text'}
                  </button>
                ))}
              </div>
              {newConnType === 'SOCIAL' && (
                <select
                  className="input"
                  value={newConnPlatform}
                  onChange={e => setNewConnPlatform(e.target.value)}
                  style={{ marginBottom: 8 }}
                >
                  <option value="">Platform</option>
                  {SOCIAL_PLATFORMS.map(p => (
                    <option key={p.key} value={p.key}>{p.name}</option>
                  ))}
                </select>
              )}
              <input
                className="input"
                value={newConnValue}
                onChange={e => {
                  const v = e.target.value;
                  setNewConnValue(v); setNewConnError('');
                  // Auto-switch the dropdown when we can tell (a link, or a $cashtag).
                  if (newConnType === 'SOCIAL') { const d = detectPlatform(v); if (d) setNewConnPlatform(d); }
                }}
                onKeyDown={e => e.key === 'Enter' && handleAddConn()}
                placeholder={
                  newConnType === 'PROFILE' ? 'Your Top 8 username'
                  : newConnType === 'TEXT' ? 'Phone number'
                  : 'Username, $cashtag, or full link'
                }
                autoFocus
              />
              {newConnError && <div style={{ fontSize: 12, color: 'var(--bad)', marginTop: 5 }}>{newConnError}</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => { setShowAddConn(false); setNewConnValue(''); setNewConnError(''); }}>Cancel</button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleAddConn}
                  disabled={!newConnValue.trim() || newConnSearching}
                >
                  {newConnSearching ? <span className="spinner" style={{ width: 12, height: 12 }} /> : 'Add'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Invite section */}
        {!hasYt8 && (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            {!showInvite ? (
              <button
                className="btn btn-block"
                style={{ justifyContent: 'center' }}
                onClick={() => { setShowInvite(true); handleGenerateInvite(); }}
              >
                Invite {entry.displayName || 'them'} to Your Top 8
              </button>
            ) : inviteLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
                <span className="spinner" />
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Share your invite link:</div>
                <div style={{
                  background: 'var(--card-2)', border: '1px solid var(--border)',
                  borderRadius: 8, padding: '8px 12px', fontSize: 12,
                  color: 'var(--muted)', wordBreak: 'break-all', marginBottom: 10,
                }}>
                  {inviteUrl}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {SHARE_CHANNELS.map(ch => (
                    <button
                      key={ch.id}
                      className="btn btn-sm"
                      onClick={() => shareInvite(ch.id, inviteUrl, currentUserProfile?.displayName || 'Someone', inviteUrl, setInviteCopied)}
                    >
                      {ch.id === 'copy' && inviteCopied ? 'Copied!' : ch.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {hasYt8 && (
          <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--muted)', marginTop: 16 }}>
            ✓ Linked to Your Top 8. Notifications active.
          </p>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={!displayName.trim() || saving}
            style={{ flex: 2 }}
          >
            {saving
              ? <span className="spinner" style={{ width: 16, height: 16, borderTopColor: '#0d0d0d' }} />
              : 'Save'
            }
          </button>
        </div>

        {onRemove && (
          <button
            onClick={() => onRemove(entry.id)}
            disabled={saving}
            style={{
              display: 'block', width: '100%', marginTop: 12, padding: 8,
              background: 'none', border: 'none', color: 'var(--bad)',
              fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font)',
            }}
          >
            Remove from Top 8
          </button>
        )}
      </div>
    </div>
  );
}

export default PersonConfigModal;
